(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────
  const GRID_SELECTOR      = '#CollectionAjaxContent .collection-grid__wrapper > .grid.grid--uniform';          // product grid
  const CARD_SELECTOR      = 'div.grid__item.grid-product';  // individual cards
  const PAGINATION_SELECTOR = '#CollectionPagination';
  const NEXT_LINK_SELECTOR  = '[data-infinite-next]';
  const SKELETON_COUNT      = 4;  // skeletons shown while loading

  // ── State ────────────────────────────────────────────────────────────────
  let isFetching  = false;
  let nextUrl     = null;
  let observer    = null;
  let sentinel    = null;
  let requestController = null;

  // ── Skeleton markup ──────────────────────────────────────────────────────
  function createSkeletons() {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < SKELETON_COUNT; i++) {
      const el = document.createElement('div');
      el.className = 'grid__item grid-product small--one-half medium-up--one-quarter infinite-skeleton';
      el.innerHTML = `
        <div class="grid-product__content">
          <div class="grid__item-image-wrapper">
            <div class="grid-product__image-mask">
              <div class="grid__image-ratio grid__image-ratio--portrait infinite-skeleton__image"></div>
            </div>
          </div>
          <div class="infinite-skeleton__title"></div>
          <div class="infinite-skeleton__price"></div>
        </div>`;
      frag.appendChild(el);
    }
    return frag;
  }

  function removeSkeletons() {
    document.querySelectorAll('.infinite-skeleton').forEach(el => el.remove());
  }

  // ── Sentinel (invisible div at bottom of grid) ───────────────────────────
  function createSentinel() {
    const el = document.createElement('div');
    el.id = 'infinite-sentinel';
    el.style.cssText = 'height:1px;width:100%;';
    return el;
  }

  // ── Core fetch & inject ──────────────────────────────────────────────────
  async function loadNextPage() {
    if (isFetching || !nextUrl) return;
    isFetching = true;

    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) { isFetching = false; return; }

    // Show skeletons
    grid.appendChild(createSkeletons());

    const controller = new AbortController();
    requestController = controller;

    try {
      const response = await fetch(nextUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('Network response was not ok');
      const html = await response.text();
      if (requestController !== controller || !grid.isConnected) return;

      const parser   = new DOMParser();
      const doc      = parser.parseFromString(html, 'text/html');

      const fetchedGrid = doc.querySelector(GRID_SELECTOR);
      if (!fetchedGrid) throw new Error('Collection product grid was not found');
      const newCards = fetchedGrid.querySelectorAll(CARD_SELECTOR);

      removeSkeletons();
      newCards.forEach(card => grid.appendChild(card));

      // Restore quick view and animations using the theme's existing lifecycle.
      if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
        window.theme.reinitProductGridItem(grid);
      }

      // Update nextUrl from the fetched page's pagination
      const newNextLink = doc.querySelector(PAGINATION_SELECTOR + ' ' + NEXT_LINK_SELECTOR);
      nextUrl = newNextLink ? newNextLink.getAttribute('href') : null;

      // Hide pagination on current page since we're handling it
      const pagination = document.querySelector(PAGINATION_SELECTOR);
      if (pagination) pagination.style.display = 'none';

      // If no more pages, disconnect observer
      if (!nextUrl) {
        observer.disconnect();
        if (sentinel) sentinel.remove();
      }

    } catch (err) {
      if (requestController !== controller || err.name === 'AbortError') return;
      removeSkeletons();
      if (observer) observer.disconnect();
      if (sentinel) sentinel.remove();
      const pagination = document.querySelector(PAGINATION_SELECTOR);
      if (pagination) pagination.style.display = '';
    } finally {
      if (requestController === controller) {
        requestController = null;
        isFetching = false;
      }
    }
  }

  function init() {
    if (requestController) requestController.abort();
    requestController = null;
    if (observer) observer.disconnect();
    if (sentinel) sentinel.remove();
    removeSkeletons();
    observer = null;
    sentinel = null;
    nextUrl = null;
    isFetching = false;

    const grid = document.querySelector(GRID_SELECTOR);
    const pagination = document.querySelector(PAGINATION_SELECTOR);
    const nextLink = pagination && pagination.querySelector(NEXT_LINK_SELECTOR);
    if (pagination) pagination.style.display = '';

    if (!grid || !nextLink || !('IntersectionObserver' in window)) return;
    nextUrl = nextLink.getAttribute('href');

    // Hide the regular pagination
    if (pagination) pagination.style.display = 'none';

    // Place sentinel after the grid
    sentinel = createSentinel();
    grid.parentNode.insertBefore(sentinel, grid.nextSibling);

    // Watch sentinel
    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: '200px' }  // start loading 200px before sentinel is visible
    );

    observer.observe(sentinel);
  }

  // ── Skeleton CSS (injected once) ─────────────────────────────────────────
  function injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .infinite-skeleton__image {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: infinite-shimmer 1.4s infinite;
      }
      .infinite-skeleton__title,
      .infinite-skeleton__price {
        height: 14px;
        border-radius: 4px;
        margin: 8px 0 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: infinite-shimmer 1.4s infinite;
      }
      .infinite-skeleton__price { width: 50%; margin-top: 6px; }
      @keyframes infinite-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `;
    document.head.appendChild(style);
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  injectStyles();
  document.addEventListener('collection:reloaded', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();